// Used with the Field to visualize a game on the field timeline

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import GameBlockEdit from './GameBlockEdit';
import GameBlockScore from './GameBlockScore';
import Spinner from './Spinner';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { tournamentGamesDelete } from '../utils/firebase';
import {
  scheduleStartHour,
  scheduleEndHour,
  scheduleStepMin,
  timeSlotHeight,
} from '../utils/consts';
import { getTeamDivision, timeStr } from '../utils/misc';
import { GameTypes } from '../utils/enums';
import placeholderTeam from '../assets/images/placeholder-team.png';
import './GameBlock.scss';

export const ItemTypes = {
  GAME_BLOCK: 'GAME_BLOCK',
  RESIZE_HANDLE: 'RESIZE_HANDLE',
};

function getTimeSlotIndex(timeStr) {
  const [inputHour, inputMin] = timeStr.split(':').map(Number);
  const totalInputMinutes = inputHour * 60 + inputMin;
  const scheduleStartMinutes = scheduleStartHour * 60;
  const scheduleEndMinutes = scheduleEndHour * 60;

  if (
    totalInputMinutes < scheduleStartMinutes ||
    totalInputMinutes >= scheduleEndMinutes
  )
    return -1; // Out of schedule

  return Math.floor(
    (totalInputMinutes - scheduleStartMinutes) / scheduleStepMin,
  );
}

export default function GameBlock({
  id,
  game,
  color,
  setBracketsPopup,
  onSearchTermClick,
  onSearchHoverChange,
  activeSearchHoverKey,
  highlighted = false,
  preview = false,
}) {
  const [spinner, setSpinner] = useState(false);

  const {
    teamsById,
    refereesById,
    userIsAdmin,
    userIsFieldManager,
    coachesByTeamId,
  } = useFirebase();

  const { openModal } = useModal();
  const { start, duration, score1, score2, round, placement, division, type } =
    game;

  const team1 = useMemo(
    () => teamsById.get(game.team1),
    [game.team1, teamsById],
  );

  const team2 = useMemo(
    () => teamsById.get(game.team2),
    [game.team2, teamsById],
  );

  const gameDivision = useMemo(() => {
    return getTeamDivision(team1 || team2) || division || null;
  }, [team1, team2, division]);

  const playoffStage = round;

  const gameRefEntries = useMemo(
    () =>
      (game.referees || [])
        .map((id) => refereesById.get(id))
        .filter(Boolean)
        .map((ref) => ({ id: ref.id, name: ref.name })),
    [refereesById, game.referees],
  );

  // "Not assigned" answers the uids, not the names: a referee whose account is
  // gone resolves to nothing, and claiming the game has no referee would lie
  const hasReferees = (game.referees || []).length > 0;

  // Nameless coaches carry ids for the busy checks, but render as a blank line
  const namedCoaches = (teamId) =>
    (coachesByTeamId.get(teamId) || []).filter((coach) => coach.name);

  const team1CoachEntries = namedCoaches(game.team1);
  const team2CoachEntries = namedCoaches(game.team2);

  const canDragBlocks = userIsAdmin;

  const [currentY, setCurrentY] = useState(null);
  const [rectCoords, setRectCoords] = useState({ top: null, bottom: null });
  const contentRef = useRef(null);
  const blockRef = useRef(null);

  const { top, bottom } = rectCoords;

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: ItemTypes.GAME_BLOCK,
    item: () => {
      const rect = blockRef.current?.getBoundingClientRect();
      return {
        id: game.id,
        type: ItemTypes.GAME_BLOCK,
        game,
        tournamentId: id,
        color,
        width: rect?.width,
      };
    },
    canDrag: () => canDragBlocks,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Suppress the unreliable native HTML5 drag preview; GameDragLayer renders
  // a custom preview positioned from the same offset the drop math uses.
  useEffect(() => {
    if (preview) return;
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, [previewRef, preview]);

  // Stable ref so react-dnd doesn't rebind the drag source on every render.
  const setBlockRef = useCallback(
    (node) => {
      dragRef(node);
      blockRef.current = node;
    },
    [dragRef],
  );

  const [{ isResizingTop }, dragTopRef] = useDrag({
    type: ItemTypes.RESIZE_HANDLE,
    item: { id: game.id, resizeType: 'start' },
    canDrag: () => canDragBlocks,
    collect: (monitor) => ({
      isResizingTop: monitor.isDragging(),
    }),
  });

  const [{ isResizingBottom }, dragBottomRef] = useDrag({
    type: ItemTypes.RESIZE_HANDLE,
    item: { id: game.id, resizeType: 'end' },
    canDrag: () => canDragBlocks,
    collect: (monitor) => ({
      isResizingBottom: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    const handleMouseMove = (event) => setCurrentY(event.pageY);

    const removeListener = () => {
      setCurrentY(null);
      document.removeEventListener('dragover', handleMouseMove);
    };

    if (isResizingTop || isResizingBottom) {
      document.addEventListener('dragover', handleMouseMove);
    } else removeListener();

    return () => removeListener();
  }, [isResizingTop, isResizingBottom]);

  const updateRectCoords = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const { top, bottom } = el.getBoundingClientRect();
    setRectCoords({
      top: top + window.scrollY,
      bottom: bottom + window.scrollY,
    });
  }, []);

  useEffect(() => {
    updateRectCoords();
  }, [start, duration, updateRectCoords]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const scrollContainer = el.closest('.timeline-fields-wrap');
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', updateRectCoords);
    return () => {
      scrollContainer.removeEventListener('scroll', updateRectCoords);
    };
  }, [updateRectCoords]);

  const deltaY = useMemo(() => {
    if (!currentY) return null;
    if (isResizingTop) return currentY - top;
    if (isResizingBottom) return currentY - bottom;
    return null;
  }, [isResizingTop, isResizingBottom, currentY, top, bottom]);

  // Snap dragging/resizing to nearest time slot
  const baseTop =
    getTimeSlotIndex(start) * timeSlotHeight + (isResizingTop ? deltaY : 0);

  const baseHeight =
    (duration / scheduleStepMin) * timeSlotHeight +
    (isResizingBottom ? deltaY : 0) -
    (isResizingTop ? deltaY : 0);

  const snappedTop =
    Math.floor((baseTop + timeSlotHeight / 2) / timeSlotHeight) *
    timeSlotHeight;

  const snappedHeight =
    Math.floor((baseHeight + timeSlotHeight / 2) / timeSlotHeight) *
    timeSlotHeight;

  const onRemove = async () => {
    try {
      setSpinner(true);
      await tournamentGamesDelete({
        id,
        gameIds: [game.id],
      });
    } catch (error) {
      console.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const handleSetScore = (e) => {
    e.stopPropagation();
    openModal({
      header: 'Set score',
      body: <GameBlockScore id={id} game={game} />,
      ancorRef: contentRef,
    });
  };

  const handleEditGame = (e) => {
    e.stopPropagation();
    openModal({
      header: 'Edit game',
      body: (
        <GameBlockEdit
          id={id}
          game={game}
          setBracketsPopup={setBracketsPopup}
        />
      ),
      ancorRef: contentRef,
    });
  };

  const handleRemoveGame = (e) => {
    e.stopPropagation();
    openModal({
      header: 'Remove game',
      body: (
        <p className='font-16'>Are you sure you want to remove the game?</p>
      ),
      buttons: [
        {
          text: 'Cancel',
          className: 'bw',
        },
        {
          text: 'Remove',
          className: 'danger',
          onClick: onRemove,
        },
      ],
      ancorRef: contentRef,
    });
  };

  const handleBracketsPopup = (e) => {
    if (userIsFieldManager) return;
    e.stopPropagation();
    setBracketsPopup({
      highlightRound: game.round,
      highlightPlacement: game.placement,
      initDivision: game.division,
    });
  };

  const handleSearchTrigger = useCallback(
    (e, value) => {
      e.stopPropagation();
      if (!onSearchTermClick) return;

      const text = String(value ?? '').trim();
      if (!text) return;

      onSearchTermClick(text);
    },
    [onSearchTermClick],
  );

  const handleSearchHoverStart = useCallback(
    (hoverKey) => {
      onSearchHoverChange(hoverKey);
    },
    [onSearchHoverChange],
  );

  const handleSearchHoverEnd = useCallback(() => {
    onSearchHoverChange(null);
  }, [onSearchHoverChange]);

  const handleSearchTriggerKeyDown = useCallback(
    (e, value) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      handleSearchTrigger(e, value);
    },
    [handleSearchTrigger],
  );

  const renderSearchTrigger = useCallback(
    ({ value, hoverKey }) => {
      if (userIsFieldManager) return <span>{value}</span>;

      return (
        <span
          className={
            'search-term-trigger' +
            (hoverKey && activeSearchHoverKey === hoverKey ? ' active' : '')
          }
          role='button'
          tabIndex={0}
          onClick={(e) => handleSearchTrigger(e, value)}
          onKeyDown={(e) => handleSearchTriggerKeyDown(e, value)}
          onMouseEnter={() => handleSearchHoverStart(hoverKey)}
          onMouseLeave={handleSearchHoverEnd}
          onFocus={() => handleSearchHoverStart(hoverKey)}
          onBlur={handleSearchHoverEnd}
        >
          {value}
        </span>
      );
    },
    [
      userIsFieldManager,
      activeSearchHoverKey,
      handleSearchTrigger,
      handleSearchTriggerKeyDown,
      handleSearchHoverStart,
      handleSearchHoverEnd,
    ],
  );

  return (
    <div
      className={`game-block ${canDragBlocks ? 'draggable' : ''} ${
        isDragging ? 'dragging' : ''
      } ${isResizingTop || isResizingBottom ? 'resizing' : ''}${
        highlighted ? ' highlighted' : ''
      }${preview ? ' preview' : ''}`}
      data-game-id={preview ? undefined : game.id}
      style={
        preview
          ? { height: snappedHeight }
          : {
              top: snappedTop,
              height: snappedHeight,
            }
      }
      ref={preview ? undefined : setBlockRef}
      onClick={
        preview
          ? undefined
          : userIsFieldManager
            ? handleSetScore
            : userIsAdmin
              ? handleEditGame
              : undefined
      }
    >
      <div
        className='content-wrap'
        ref={contentRef}
        style={{ borderColor: color, backgroundColor: `${color}1A` }}
      >
        <div
          className={`resize-handle top${canDragBlocks ? ' draggable' : ''}`}
          ref={preview ? undefined : dragTopRef}
          style={{ backgroundColor: color }}
        />
        <p>{timeStr(start, duration)}</p>
        <p className='division'>{gameDivision}</p>
        <div className='teams box'>
          {round !== null && placement !== null && (
            <button
              className='round-game transparent small'
              onClick={handleBracketsPopup}
            >{`Playoff: ${
              playoffStage || `Round ${round}`
            }, game ${placement}`}</button>
          )}
          {type === GameTypes.POOLGAME && (
            <p className='pool-game'>Pool game</p>
          )}
          <div className='teams-score-wrap'>
            <div className='team first'>
              <div className='team-emblem-row'>
                {team1 && (
                  <div
                    className='bg-image wh-32'
                    style={{
                      backgroundImage: `url(${team1.imgThumbUrl || placeholderTeam})`,
                    }}
                  />
                )}
                <div className='wrap'>
                  <p className='font-medium'>
                    {team1?.name
                      ? renderSearchTrigger({
                          value: team1.name,
                          hoverKey: `team:${game.team1}`,
                        })
                      : 'TBD'}
                  </p>
                  {team1CoachEntries.map((coach) => (
                    <p key={`${game.id}-team1-${coach.id}`}>
                      {renderSearchTrigger({
                        value: coach.name,
                        hoverKey: `coach:${coach.id}`,
                      })}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <p className='font-medium score'>
              {score1 !== null || score2 !== null
                ? score1 + ' : ' + score2
                : ' - '}
            </p>
            <div className='team second'>
              <div className='team-emblem-row'>
                {team2 && (
                  <div
                    className='bg-image wh-32'
                    style={{
                      backgroundImage: `url(${team2.imgThumbUrl || placeholderTeam})`,
                    }}
                  />
                )}
                <div className='wrap'>
                  <p className='font-medium'>
                    {team2?.name
                      ? renderSearchTrigger({
                          value: team2.name,
                          hoverKey: `team:${game.team2}`,
                        })
                      : 'TBD'}
                  </p>
                  {team2CoachEntries.map((coach) => (
                    <p key={`${game.id}-team2-${coach.id}`}>
                      {renderSearchTrigger({
                        value: coach.name,
                        hoverKey: `coach:${coach.id}`,
                      })}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className='referees'>
          Referees:{' '}
          {!hasReferees
            ? 'not assigned'
            : gameRefEntries.length === 0
              ? '—'
              : gameRefEntries.map((ref, index) => (
                  <span key={`${game.id}-ref-${ref.id}`}>
                    {index > 0 ? ', ' : ''}
                    {renderSearchTrigger({
                      value: ref.name,
                      hoverKey: `ref:${ref.id}`,
                    })}
                  </span>
                ))}
        </p>
        {userIsAdmin && (
          <div className='btns-wrap'>
            <button
              className='score-btn bw small fa-calculator'
              onClick={handleSetScore}
              aria-label='Set score'
            />
            <button
              className='edit-btn bw small fa-pen'
              onClick={handleEditGame}
              aria-label='Edit game'
            />
            <button
              className='delete-btn small bw'
              onClick={handleRemoveGame}
              aria-label='Delete game'
            >
              <span className='danger fa-trash' />
            </button>
          </div>
        )}
        <Spinner show={spinner} />
        <div
          className={`resize-handle bottom${canDragBlocks ? ' draggable' : ''}`}
          ref={preview ? undefined : dragBottomRef}
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
}
